import { Box } from '@hover/blueprint';
import React, { useEffect } from 'react';
import { Redirect } from 'react-router';

import { useLogout } from 'src/modules/logout/hooks';
import { useAuthenticateWithCookie } from 'src/modules/propertyDetails/hooks/useAuthenticateWithCookie';
import {
  DelayedLoader,
  ImpersonatorBanner,
  PaymentBannerV1,
} from 'src/shared/components';
import { NavBar, NavigationBar } from 'src/shared/components/NavBar';
import { PAYMENT_BANNER_EVENT_NAMES } from 'src/shared/components/PaymentBanner/types';
import { FeatureFlag } from 'src/shared/constants';
import { useProfileQuery } from 'src/shared/graphql/generated';
import { isServer } from 'src/shared/helpers/environment';
import { loginPathWithRedirectTo } from 'src/shared/helpers/routing';
import { useCurrentPage, useLocation, useTreatment } from 'src/shared/hooks';
import { useAuthExperience } from 'src/shared/hooks/useAuthExperience';
import { BrandProvider } from 'src/shared/providers/Brand';
import { moduleViewed } from 'src/shared/telemetry';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

export const AuthenticatedLayout = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const { loading, user } = useAuthExperience();
  const { data: profileData } = useProfileQuery();

  const { pathname } = useLocation();
  const shouldShowWhiteNavBar = isEnabled(
    useTreatment(FeatureFlag.Web.JOBS_PAGE as string, {
      kind: profileData?.profile.user.orgs.some(
        userOrg => userOrg?.partner?.kind === 'INSURANCE',
      )
        ? 'INSURANCE'
        : 'STANDARD',
    }),
  );
  const currentPage = useCurrentPage();
  const [logout, { isPending: isLogoutPending }] = useLogout();

  const bannerDetails = user?.orgs?.[0]?.paymentBanner;

  useEffect(() => {
    if (bannerDetails && currentPage) {
      moduleViewed({
        page_or_screen_name: currentPage,
        feature: `${
          PAYMENT_BANNER_EVENT_NAMES[bannerDetails.kind]
        } credit card banner`,
      });
    }
  }, [bannerDetails, currentPage]);

  if (!loading && !user) {
    // If we fail to authenticate on the server, then don't try
    // to SSR and return empty body. Let the client side run and
    // see if authentication still fails. If so, then redirect user.
    // The reason for this is that SSR uses a Hyperion cookie for auth
    // and client side uses JWT token. If Hyperion cookie is bad/missing/expired
    // then without this logic, the users ends up in an infinite redirect
    // because the client is authenticated, but their server session is not.
    return isServer() ? (
      <div />
    ) : (
      <Redirect to={loginPathWithRedirectTo(pathname)} />
    );
  }

  return (
    <BrandProvider>
      <Box
        flexDirection="column"
        maxWidth="100%"
        minHeight="100vh"
        width="100vw"
      >
        <ImpersonatorBanner />
        {bannerDetails && <PaymentBannerV1 details={bannerDetails} />}
        {shouldShowWhiteNavBar ? (
          <NavigationBar onLogout={logout} />
        ) : (
          <NavBar onLogout={logout} />
        )}
        <Box flex={1} justifyContent="center">
          {loading || isLogoutPending ? (
            <Box
              alignItems="center"
              height="100vh"
              justifyContent="center"
              minW="100vw"
            >
              <DelayedLoader delay={1000} />
            </Box>
          ) : (
            children
          )}
        </Box>
      </Box>
    </BrandProvider>
  );
};

export const AuthenticatedLayoutContainer = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const hasValidJwt = useAuthenticateWithCookie();

  // If they don't have a valid cookie, the hook will redirect them to login.
  // If they have a valid cookie, the client will update the user's JWTs and
  // render the page.
  if (!hasValidJwt) {
    return <div />;
  }

  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};
